import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const statusColors = {
  available: { text: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Available' },
  'in use': { text: 'text-blue-500', bg: 'bg-blue-500/10', label: 'In Use' },
  maintenance: { text: 'text-amber-500', bg: 'bg-amber-500/10', label: 'Maintenance' },
  unavailable: { text: 'text-red-500', bg: 'bg-red-500/10', label: 'Unavailable' },
};

const categoryIcons = {
  employees: 'Users',
  equipment: 'Wrench',
  trucks: 'Truck',
  contractors: 'UserCog',
  teams: 'Users2',
};

const ResourceCategoryRow = ({ category, resources }) => {
  const [expanded, setExpanded] = useState(false);
  const available = resources?.filter(r => r?.status?.toLowerCase() === 'available')?.length;
  const total = resources?.length;
  const icon = categoryIcons?.[category] || 'Package';
  const label = category?.charAt(0)?.toUpperCase() + category?.slice(1);

  return (
    <div className="border-b border-border last:border-0">
      <button
        className="w-full flex items-center justify-between py-3 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
            <Icon name={icon} size={16} className="text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{label}</p>
            <p className="text-xs text-muted-foreground">{available}/{total} available</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full"
              style={{ width: total > 0 ? `${(available / total) * 100}%` : '0%' }}
            />
          </div>
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-muted-foreground" />
        </div>
      </button>
      {expanded && resources?.length > 0 && (
        <div className="pb-2 pl-11 space-y-1">
          {resources?.slice(0, 4)?.map(r => {
            const statusKey = r?.status?.toLowerCase();
            const sc = statusColors?.[statusKey] || statusColors?.available;
            return (
              <div key={r?.id} className="flex items-center justify-between py-1">
                <p className="text-xs text-foreground truncate flex-1 mr-2">{r?.name}</p>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sc?.bg} ${sc?.text}`}>
                  {sc?.label}
                </span>
              </div>
            );
          })}
          {resources?.length > 4 && (
            <p className="text-xs text-muted-foreground pt-1">+{resources?.length - 4} more</p>
          )}
        </div>
      )}
    </div>
  );
};

const ResourceStatusPanel = ({ resources, loading }) => {
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-2xl p-4">
        <div className="h-5 bg-muted rounded w-36 mb-4 animate-pulse" />
        {[1, 2, 3]?.map(i => (
          <div key={i} className="py-3 border-b border-border last:border-0 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-muted rounded-lg" />
              <div>
                <div className="h-4 bg-muted rounded w-20 mb-1" />
                <div className="h-3 bg-muted rounded w-16" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const categories = ['employees', 'equipment', 'trucks', 'contractors', 'teams'];
  const grouped = categories?.reduce((acc, cat) => {
    const items = resources?.filter(r => r?.category === cat);
    if (items?.length > 0) acc[cat] = items;
    return acc;
  }, {});

  const conflicts = resources?.filter(r => r?.has_conflict)?.length || 0;

  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-bold text-foreground">Resource Status</h2>
        {conflicts > 0 && (
          <div className="flex items-center gap-1 text-xs font-medium text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full">
            <Icon name="AlertTriangle" size={12} />
            <span>{conflicts} conflict{conflicts > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>
      {Object.keys(grouped)?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Icon name="Package" size={32} className="text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No resources found</p>
        </div>
      ) : (
        <div>
          {Object.entries(grouped)?.map(([cat, items]) => (
            <ResourceCategoryRow key={cat} category={cat} resources={items} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourceStatusPanel;
